// Captain identity derivation from a uint32 seed (spec-captain §Runtime).
// Same seed → same captain, always: name, pronoun set, temperament, atlas
// part indices and skin-tone index are all drawn from one mulberry32 stream.
// mulberry32 is inlined here (identical algorithm to sim/rng.ts,
// GDD Appendix A §A.1) — the captain feature deliberately does not import
// from the sim so it stays usable from loadout/UI without sim state.

import { NAME_BANK } from './dictionary.js';
import type { PronounSet, Temperament } from './dictionary.js';

/**
 * Atlas slot part counts (spec-captain §Atlas). Seed owns index selection;
 * the compositor re-clamps against the real atlas.json counts when one is
 * present, so a shipped atlas with fewer parts degrades gracefully.
 */
export const ATLAS_SLOT_COUNTS = {
  face: 4,
  eyes: 6,
  brows: 4,
  nose: 5,
  mouth: 6,
  hair: 8,
  beard: 3,
  headwear: 5,
  uniform: 4,
  mark: 5
} as const;

export type AtlasSlot = keyof typeof ATLAS_SLOT_COUNTS;

export const SKIN_TONE_COUNT = 4;

export interface CaptainRecord {
  /** uint32 the record was derived from (normalized with >>> 0). */
  seed: number;
  /** Display name: "First Last", or 'First "Callsign" Last' (~1 in 3). */
  name: string;
  pronouns: PronounSet;
  temperament: Temperament;
  /** Atlas part index per slot, each in [0, ATLAS_SLOT_COUNTS[slot]). */
  parts: Record<AtlasSlot, number>;
  /** Index into the compositor's 4-tone skin ramp. */
  skinTone: number;
}

const TEMPERAMENTS: readonly Temperament[] = ['stoic', 'hothead', 'smooth', 'worrier', 'deadpan'];
const PRONOUN_SETS: readonly PronounSet[] = ['he', 'she', 'they'];
// Fixed draw order — the record depends on it, never reorder.
const SLOT_ORDER = Object.keys(ATLAS_SLOT_COUNTS) as AtlasSlot[];

// Exact mulberry32 per GDD Appendix A §A.1 (inline copy; see header).
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6D2B79F5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Derive the full captain record from a seed. Pure; safe to call anywhere. */
export function captainFromSeed(seed: number): CaptainRecord {
  const rng = mulberry32(seed);
  const int = (n: number): number => Math.floor(rng() * n);
  const pick = <T,>(arr: readonly T[], fallback: T): T =>
    arr.length > 0 ? arr[int(arr.length)]! : fallback;

  // Pronouns come first: the first-name bank is keyed by pronoun set.
  const pronouns = PRONOUN_SETS[int(PRONOUN_SETS.length)]!;
  const temperament = TEMPERAMENTS[int(TEMPERAMENTS.length)]!;
  const first = pick(NAME_BANK.first[pronouns], 'Ash');
  const last = pick(NAME_BANK.last, 'Harrow');
  // Roughly a third of captains go by a callsign — 'Vera "Sable" Harrow'.
  const withCallsign = rng() < 1 / 3;
  const name = withCallsign
    ? `${first} "${pick(NAME_BANK.callsigns, 'Ember')}" ${last}`
    : `${first} ${last}`;

  const parts = {} as Record<AtlasSlot, number>;
  for (const slot of SLOT_ORDER) {
    parts[slot] = int(ATLAS_SLOT_COUNTS[slot]);
  }
  return { seed: seed >>> 0, name, pronouns, temperament, parts, skinTone: int(SKIN_TONE_COUNT) };
}

/** Crypto-random uint32 seed for the loadout re-roll (hire) button. */
export function rollCaptainSeed(): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0]!;
}
